import { AnchorButton, Classes, Navbar } from '@blueprintjs/core';
import { createSelector } from '@reduxjs/toolkit';
import classNames from 'classnames';
import cloneDeep from 'lodash/cloneDeep';
import * as React from 'react';
import { v4 as uuid } from 'uuid';
import { getIntentClassName } from 'vet-bones/utils';
import { usePortalFeatures } from 'vet-bones/utils/portal';
import { usePortalVoiceboxConversation } from 'vet-bones/utils/voicebox';

import { projectsActionCreators } from 'src/actions/projects/actionCreators';
import { ProjectResourceType } from 'src/constants/project';
import { TEST_IDS } from 'src/constants/testIds';
import { NodeCategory } from 'src/containers/ProjectDiagram/constants/categories';
import { DiagramTransaction } from 'src/containers/ProjectDiagram/constants/transactions';
import { useDiagram } from 'src/containers/ProjectDiagram/hooks/useDiagramValue';
import { Diagram } from 'src/containers/ProjectDiagram/types/diagram';
import { ModelData } from 'src/containers/ProjectDiagram/types/model';
import { commitDiagramWhileSkippingUndoManager } from 'src/containers/ProjectDiagram/utils/commitDiagramModel';
import {
  isDeletedLinkData,
  isDeletedNodeData,
} from 'src/containers/ProjectDiagram/utils/data';
import { setDataProperty } from 'src/containers/ProjectDiagram/utils/setDataProperty';
import {
  diagramSnapshotSelector,
  isDraftDiagramSelector,
} from 'src/selectors/diagramSnapshot';
import { memoizeSelectorFactory } from 'src/selectors/memoizeSelectorFactory';
import { projectSelector } from 'src/selectors/project';
import { projectCompetencyQuestionsSelector } from 'src/selectors/projectCompetencyQuestions';
import * as copy from 'src/templates/copy';
import { useIsMounted } from 'src/utils/hooks/useIsMounted';
import { useIsVoiceboxEnabled } from 'src/utils/hooks/useIsVoiceboxEnabled';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';
import { useEvaluateSummary } from 'src/utils/voicebox/hooks/useEvaluateSummary';

const conversationIdSelector = memoizeSelectorFactory((projectId: string) =>
  createSelector(
    projectSelector(projectId),
    (project) => project?.voiceboxConversationId || ''
  )
);

export const VoiceboxFooter = () => {
  const dispatch = useReduxDispatch();
  const isMounted = useIsMounted();
  const { projectId } = useRouterParams();

  const liveDiagram = useDiagram();
  const diagram = useReduxSelector(diagramSnapshotSelector(projectId));
  const isDraftDiagram = useReduxSelector(isDraftDiagramSelector(projectId));
  const conversationId = useReduxSelector(conversationIdSelector(projectId));
  const conversation = usePortalVoiceboxConversation(conversationId, {
    enabled: false,
  });

  const portalFeatures = usePortalFeatures();
  const isVoiceboxEnabled = useIsVoiceboxEnabled();
  const project = useReduxSelector(projectSelector(projectId));
  const useVoiceboxSuggestions =
    Boolean(isVoiceboxEnabled && portalFeatures.voiceboxSuggestions) &&
    !project?.disableVoiceboxSuggestions;

  const questions = useReduxSelector(
    projectCompetencyQuestionsSelector(projectId)
  );
  const { cancelEvaluateSummary, evaluateSummary } = useEvaluateSummary();

  // Clear draftData on the live GoJS model too: until the accept/reject rebuild
  // lands, a commit against it (e.g. the async evaluation below) would echo the
  // stale draftData back into Redux and re-show the footer. CLEAR_DRAFT skips
  // the echo since the accept/reject dispatch already owns Redux.
  const clearLiveDraftData = () => {
    if (
      !liveDiagram ||
      liveDiagram.headless ||
      !liveDiagram.model.modelData.draftData
    ) {
      return;
    }
    commitDiagramWhileSkippingUndoManager(
      liveDiagram as Diagram,
      (model) =>
        setDataProperty<ModelData>(model)(
          model.modelData,
          'draftData',
          undefined
        ),
      DiagramTransaction.CLEAR_DRAFT
    );
  };

  const handleAcceptVoicebox = async () => {
    if (!isDraftDiagram || !diagram) {
      return;
    }

    const updatedModel = cloneDeep(diagram.model);
    updatedModel.nodeDataArray = updatedModel.nodeDataArray.filter(
      (data) => !isDeletedNodeData(data)
    );
    updatedModel.linkDataArray = updatedModel.linkDataArray
      .filter((data) => !isDeletedLinkData(data))
      .map((link) => ({
        ...link,
        points: [],
      }));
    updatedModel.modelData.draftData = undefined;

    const { data } = await conversation?.refetch();
    if (!isMounted.current) {
      return;
    }

    clearLiveDraftData();

    const isSummaryOutdated =
      !project?.voiceboxEvaluationSummary ||
      !project?.voiceboxEvaluatedSummary ||
      questions.some(
        (question) =>
          !question.evaluation ||
          !project.voiceboxEvaluatedCompetencyQuestions?.includes(question.id)
      );

    if (isSummaryOutdated && useVoiceboxSuggestions) {
      evaluateSummary(project);
    }

    dispatch(
      projectsActionCreators.acceptedProjectDraftDiagram(
        projectId,
        { id: uuid(), model: updatedModel },
        data?.messages || []
      )
    );
  };

  const handleRejectVoicebox = async () => {
    if (!isDraftDiagram || !diagram) {
      return;
    }

    const updatedModel = cloneDeep(diagram.model);
    if (!updatedModel.modelData.draftData) {
      return;
    }

    const prevNodeDataArray =
      updatedModel.modelData.draftData.prevNodeDataArray || [];
    const prevFileUploadIds = new Set(
      prevNodeDataArray
        .filter(
          (node) =>
            node.category === NodeCategory.RESOURCE &&
            node.type === ProjectResourceType.FILE_UPLOAD
        )
        .map((node) => node.value)
    );

    const currentFileUploadIds = diagram.model.nodeDataArray
      .filter(
        (node) =>
          node.category === NodeCategory.RESOURCE &&
          node.type === ProjectResourceType.FILE_UPLOAD
      )
      .map((node) => node.value);

    const fileUploadsToRemove = currentFileUploadIds.filter(
      (id) => !prevFileUploadIds.has(id)
    );

    updatedModel.nodeDataArray = (
      updatedModel.modelData.draftData.prevNodeDataArray || []
    ).filter((data) => !isDeletedNodeData(data));
    updatedModel.linkDataArray = (
      updatedModel.modelData.draftData.prevLinkDataArray || []
    ).filter((data) => !isDeletedLinkData(data));
    updatedModel.modelData = updatedModel.modelData.draftData.prevModelData;

    const { data } = await conversation?.refetch();
    if (!isMounted.current) {
      return;
    }

    clearLiveDraftData();

    // cancel any pending model summaries when preview state is closed
    cancelEvaluateSummary();

    dispatch(
      projectsActionCreators.clearedProjectVoiceboxEvaluation({
        id: projectId,
        clearSummary: true,
      })
    );

    dispatch(
      projectsActionCreators.rejectedProjectDraftDiagram(
        projectId,
        { id: uuid(), model: updatedModel },
        data?.messages || [],
        fileUploadsToRemove.length > 0 ? fileUploadsToRemove : undefined
      )
    );
  };

  if (!isDraftDiagram) {
    return null;
  }

  return (
    <Navbar
      className="sd-voicebox-footer"
      data-testid={TEST_IDS.voicebox.footer.container}
    >
      <Navbar.Group
        className={classNames(Classes.DARK, 'sd-voicebox-footer--center')}
      >
        <div>{copy.components.voicebox.footer.help}</div>
      </Navbar.Group>
      <Navbar.Group className="sd-voicebox-footer--right">
        <div className="sd-voicebox-footer__reject">
          <AnchorButton
            data-testid={TEST_IDS.voicebox.footer.reject}
            minimal
            onClick={handleRejectVoicebox}
            outlined
            text={copy.components.voicebox.footer.reject}
          />
        </div>
        <AnchorButton
          className={getIntentClassName('sd-green')}
          data-testid={TEST_IDS.voicebox.footer.accept}
          onClick={handleAcceptVoicebox}
          text={copy.components.voicebox.footer.accept}
        />
      </Navbar.Group>
    </Navbar>
  );
};
